import { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { LayoutGrid, Users, FileText, Radio, Building2, Settings as SettingsIcon, LogOut, RefreshCw, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "./ConfirmDialog";

const formatLastSync = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const isToday = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  return isToday
    ? d.toLocaleTimeString()
    : `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
};

const nav = [
  { to: "/console", label: "Overview", icon: LayoutGrid, end: true },
  { to: "/console/employees", label: "Employees", icon: Users },
  { to: "/console/sops", label: "SOP Library", icon: FileText },
  { to: "/console/devices", label: "Devices", icon: Radio },
  { to: "/console/departments", label: "Departments", icon: Building2 },
  { to: "/console/settings", label: "Settings", icon: SettingsIcon },
];

export default function ConsoleLayout() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const location = useLocation();
  const [lastSync, setLastSync] = useState(null);
  const [online, setOnline] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const poll = () => {
      api.get("/sync/status")
        .then(({ data }) => { setLastSync(data.last_sync); setOnline(data.connection === "online"); })
        .catch(() => setOnline(false));
    };
    poll();
    const interval = setInterval(poll, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    const ok = await confirm({
      title: "Sign out?",
      description: "You'll need to sign in again to access your console.",
      confirmLabel: "Sign out",
    });
    if (!ok) return;
    try { await api.post("/auth/logout"); } catch (e) { /* session already gone */ }
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-[#f9f9f8]">
      <header className="lg:hidden fixed top-0 inset-x-0 z-50 h-14 bg-white border-b border-neutral-200 flex items-center gap-3 px-4">
        <button data-testid="mobile-menu-button" onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 -ml-2 rounded-md text-neutral-600 hover:bg-neutral-100 transition-colors">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <Logo suffix="Console" />
      </header>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-30 bg-black/30" onClick={() => setMobileOpen(false)} />
      )}

      <aside className={`w-60 shrink-0 border-r border-neutral-200 bg-white flex flex-col fixed inset-y-0 left-0 z-40 transition-transform duration-200 lg:translate-x-0 ${mobileOpen ? "translate-x-0 pt-14 lg:pt-0" : "-translate-x-full"}`}>
        <div className="h-16 hidden lg:flex items-center px-5 border-b border-neutral-200">
          <Logo suffix="Console" />
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {nav.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive ? "bg-[#111] text-white" : "text-neutral-600 hover:bg-neutral-100 hover:text-[#111]"
                }`}>
              <item.icon className="w-4 h-4" /> {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-5 py-4 border-t border-neutral-200">
          <div className="flex items-center gap-2 text-xs text-neutral-500" data-testid="sync-indicator">
            <span className="relative flex h-2 w-2">
              {online === true && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-60"></span>}
              <span className={`relative inline-flex rounded-full h-2 w-2 ${online === true ? "bg-green-600" : online === false ? "bg-red-600" : "bg-neutral-300"}`}></span>
            </span>
            Local server LS-01 {online === null ? "checking…" : online ? "online" : "offline"}
          </div>
          {lastSync && formatLastSync(lastSync) && (
            <div className="flex items-center gap-1.5 mt-2 text-[11px] text-neutral-400 font-mono-data">
              <RefreshCw className="w-3 h-3" /> Last sync {formatLastSync(lastSync)}
            </div>
          )}
        </div>
        <div className="px-3 py-3 border-t border-neutral-200">
          <div className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-neutral-50 transition-colors group">
            <div className="w-9 h-9 shrink-0 rounded-full bg-[#111] text-white flex items-center justify-center text-xs font-medium">
              {(user?.name || "?").trim().charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate" data-testid="console-user-name">{user?.name}</p>
              <p className="text-xs text-neutral-500 truncate">{user?.company}</p>
            </div>
            <button onClick={handleLogout} data-testid="logout-button" title="Sign out"
              className="p-2 rounded-md text-neutral-400 hover:bg-neutral-200 hover:text-[#111] transition-colors shrink-0">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
      <main className="lg:ml-60 min-w-0 pt-14 lg:pt-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 lg:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
